import { Box, Chip, Typography } from '@mui/material';
import { colorForField } from './colors';
import { Timeline, TimelineSegmentGroup } from './Timeline';
import { isScalar, toMinutesRange, toMinutesRangeArray } from './valueKinds';

interface OutputRendererProps {
  pluginId: string;
  outputs: Record<string, unknown>;
}

/**
 * Renders one plugin's outputs without knowing its schema: minute ranges go on a 24h timeline,
 * scalars become chips, everything else is shown as compact JSON so nothing is hidden.
 */
export const OutputRenderer = ({ pluginId, outputs }: OutputRendererProps) => {
  const timeline: TimelineSegmentGroup[] = [];
  const scalars: [string, string][] = [];
  const other: [string, unknown][] = [];

  Object.entries(outputs).forEach(([field, value]) => {
    const rangeArray = toMinutesRangeArray(value);
    const singleRange = toMinutesRange(value);
    if (rangeArray) timeline.push({ label: field, ranges: rangeArray, color: colorForField(field) });
    else if (singleRange) timeline.push({ label: field, ranges: [singleRange], color: colorForField(field) });
    else if (isScalar(value)) scalars.push([field, String(value)]);
    else other.push([field, value]);
  });

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
        {pluginId}
      </Typography>
      {Object.keys(outputs).length === 0 && (
        <Typography variant="caption" color="text.secondary">
          no outputs
        </Typography>
      )}
      {timeline.length > 0 && <Timeline segments={timeline} />}
      {scalars.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
          {scalars.map(([field, value]) => (
            <Chip key={field} size="small" label={`${field}: ${value}`} />
          ))}
        </Box>
      )}
      {other.map(([field, value]) => (
        <Box key={field} sx={{ mt: 0.5 }}>
          <Typography variant="caption">{field}</Typography>
          <Box component="pre" sx={{ m: 0, p: 1, fontSize: 12, maxHeight: 240, overflow: 'auto', bgcolor: 'action.hover', borderRadius: 1 }}>
            {JSON.stringify(value, null, 2)}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
